import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, ExternalLink, CheckCircle, Eye, AlertCircle } from 'lucide-react';

const OnlineAdmissionLink = () => {
  const [copied, setCopied] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [collegeName, setCollegeName] = useState('');

  useEffect(() => {
    // Get tenant info from token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setTenantId(payload.tenant_id || '');
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    
    // Get college name from localStorage or settings
    const storedSettings = localStorage.getItem('collegeSettings');
    if (storedSettings) {
      try {
        const settings = JSON.parse(storedSettings);
        setCollegeName(settings.college_name || '');
      } catch (e) {}
    }
  }, []);

  // Get the base URL - works for both preview and production
  const getBaseUrl = () => {
    // Use REACT_APP_BACKEND_URL to determine environment
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    // If backend URL contains 'preview', we're in preview
    // Otherwise, use the current window origin for production
    if (backendUrl.includes('preview.emergentagent.com')) {
      // Extract the base preview URL pattern
      const match = backendUrl.match(/https:\/\/([^.]+)\.preview\.emergentagent\.com/);
      if (match) {
        return `https://financial-reports-15.preview.emergentagent.com`;
      }
    }
    
    // For production or local, use window.location.origin
    return window.location.origin;
  };

  // Build the admission form URL with tenant ID
  const formUrl = tenantId 
    ? `${getBaseUrl()}/public/admission-form/${tenantId}`
    : `${getBaseUrl()}/public/admission-form`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(formUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenForm = () => {
    window.open(formUrl, '_blank');
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Online Admission Form Link</h1>
        <p className="text-gray-600 mt-1">Share this link with students for online admission applications</p>
      </div>

      {/* Warning if no tenant ID */}
      {!tenantId && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Tenant ID not found</p>
            <p className="text-sm text-yellow-700">Please login again to get the correct admission form link for your institution.</p>
          </div>
        </div>
      )}

      {/* Link Card */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-xl text-blue-900">
            {collegeName ? `${collegeName} - Admission Form URL` : 'Public Admission Form URL'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* URL Display */}
            <div className="flex items-center gap-3 bg-white p-4 rounded-lg border-2 border-blue-300">
              <div className="flex-1 font-mono text-sm text-blue-700 break-all">
                {formUrl}
              </div>
              <Button
                onClick={handleCopyLink}
                className={`flex items-center gap-2 ${copied ? 'bg-green-600' : 'bg-blue-600'}`}
              >
                {copied ? (
                  <>
                    <CheckCircle size={16} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy
                  </>
                )}
              </Button>
            </div>

            {/* Tenant ID Info */}
            {tenantId && (
              <div className="text-sm text-gray-600 bg-gray-100 p-2 rounded">
                <strong>Institution ID:</strong> <code className="bg-gray-200 px-1 rounded">{tenantId}</code>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleOpenForm}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
              >
                <ExternalLink size={16} />
                Open Form in New Tab
              </Button>
              <Button
                onClick={() => window.open(`${formUrl}?preview=true`, '_blank')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Eye size={16} />
                Preview Form
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* URL Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="text-blue-900">URL Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <p className="text-gray-700">
              <strong>Preview URL:</strong> This URL will work in preview environment
            </p>
            <p className="text-gray-700">
              <strong>Production URL:</strong> After deployment, it will automatically work on production domain
            </p>
            <p className="text-gray-700">
              <strong>Tenant ID:</strong> Your institution's unique ID included in the URL - only admissions for your institution will be processed
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>How to Use</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">1</div>
              <div>
                <h3 className="font-semibold text-gray-800">Share the Link</h3>
                <p className="text-sm text-gray-600">Copy the URL and share it on your website, social media, or directly with students via email/WhatsApp</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">2</div>
              <div>
                <h3 className="font-semibold text-gray-800">Students Fill the Form</h3>
                <p className="text-sm text-gray-600">Students complete the admission form with their personal, academic details and upload required documents</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">3</div>
              <div>
                <h3 className="font-semibold text-gray-800">Online Payment</h3>
                <p className="text-sm text-gray-600">Students pay the admission fee through Razorpay payment gateway</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">4</div>
              <div>
                <h3 className="font-semibold text-gray-800">Review & Approve</h3>
                <p className="text-sm text-gray-600">Check "Pending Admissions" to review and approve applications</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card className="bg-green-50 border-green-200">
        <CardHeader>
          <CardTitle className="text-green-900">Key Features</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm text-gray-700"><strong>No Login Required</strong> - Students can apply without creating an account</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm text-gray-700"><strong>Tenant Specific</strong> - Each institution has unique URL, admissions go to correct institution</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm text-gray-700"><strong>Secure Payment</strong> - Razorpay integration with instant payment verification</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm text-gray-700"><strong>Auto Receipt</strong> - Students receive receipt after successful payment</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={18} />
              <span className="text-sm text-gray-700"><strong>Document Upload</strong> - Students can upload all required documents</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Payment Test Info */}
      <Card className="bg-yellow-50 border-yellow-300">
        <CardHeader>
          <CardTitle className="text-yellow-900">Test Mode Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-3">
            The system is currently in <strong>TEST MODE</strong>. Use these test card details for payment:
          </p>
          <div className="bg-white p-4 rounded border border-yellow-300 font-mono text-sm">
            <p><strong>Card Number:</strong> 4111 1111 1111 1111</p>
            <p><strong>Expiry:</strong> Any future date</p>
            <p><strong>CVV:</strong> Any 3 digits</p>
            <p><strong>Name:</strong> Any name</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnlineAdmissionLink;
